import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Modal';
import { PermissionHelp } from '@/features/onboarding/CameraCheck';
import { classifyMediaError, stopStream, type MediaErrorKind } from '@/lib/media';
import { queryClient } from '@/api/queryClient';
import { qk } from '@/api/queries';
import { endpoints } from '@/api';
import type { AppConfig } from '@/api/types';
import { cn } from '@/lib/cn';
import { useFaceCheck } from './useFaceCheck';
import { FACE_PRIVACY, GATE_PRESENT_MS, gatePassed } from './guard';
import { faceCheckDevOff, recentlyVerified, useFaceStore } from './store';
import { preloadFaceDetector, type DetectorLoader } from './detector';
import { requireGuidelines } from '@/features/onboarding/GuidelinesReaccept';

export { FACE_PRIVACY } from './guard';

type CamState = 'starting' | 'live' | 'error';

async function openCamera(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) throw new DOMException('getUserMedia not available', 'TypeError');
  return navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }, audio: false });
}

/** Oval framing guide; the outline fills in as the face is held for 1 s. */
function Guide({ state, progress }: { state: 'searching' | 'holding' | 'ok'; progress: number }) {
  return (
    <svg viewBox="0 0 100 120" className="pointer-events-none absolute left-1/2 top-1/2 h-[78%] -translate-x-1/2 -translate-y-1/2" aria-hidden>
      <ellipse cx="50" cy="60" rx="38" ry="50" fill="none" stroke={state === 'searching' ? 'rgba(242,238,229,0.7)' : 'rgba(24,188,66,0.35)'} strokeWidth="1.4" strokeDasharray={state === 'searching' ? '3 4' : undefined} />
      {state !== 'searching' && (
        <ellipse cx="50" cy="60" rx="38" ry="50" fill="none" stroke="#18BC42" strokeWidth="2.8" strokeLinecap="round" pathLength={1} strokeDasharray={`${progress} 1`} />
      )}
    </svg>
  );
}

export interface FaceGateProps {
  onPass: () => void;
  title?: string;
  sub?: string;
  className?: string;
  loader?: DetectorLoader;
}

/**
 * "Show us your face to start" — a local preview that passes once a face has
 * been in frame for ≥ 1 s (SPEC_V2 §7). Camera denied ⇒ explain, no matching.
 */
export function FaceGate({ onPass, title = 'Show us your face to start', sub = 'Popin calls are face to face. Look at the camera for a second.', className, loader }: FaceGateProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cam, setCam] = useState<CamState>('starting');
  const [camErr, setCamErr] = useState<MediaErrorKind | null>(null);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [passed, setPassed] = useState(false);
  const markVerified = useFaceStore((s) => s.markVerified);
  const onPassRef = useRef(onPass);
  onPassRef.current = onPass;

  const start = useCallback(async () => {
    setCam('starting');
    setCamErr(null);
    try {
      const s = await openCamera();
      stopStream(streamRef.current);
      streamRef.current = s;
      const v = videoRef.current;
      if (v) {
        v.srcObject = s;
        await v.play().catch(() => undefined);
      }
      setVideoEl(v);
      setCam('live');
    } catch (e) {
      setCamErr(classifyMediaError(e));
      setCam('error');
    }
  }, []);

  useEffect(() => {
    if (!loader) preloadFaceDetector();
    void start();
    return () => stopStream(streamRef.current);
  }, [start, loader]);

  const check = useFaceCheck(cam === 'live' ? videoEl : null, { enabled: cam === 'live' && !passed, loader });
  const ok = gatePassed(check);

  useEffect(() => {
    if (!ok || passed) return;
    setPassed(true);
    markVerified();
  }, [ok, passed, markVerified]);

  // Separate effect so flipping `passed` doesn't cancel the hand-off.
  useEffect(() => {
    if (!passed) return;
    const t = setTimeout(() => {
      stopStream(streamRef.current);
      streamRef.current = null;
      onPassRef.current();
    }, 450);
    return () => clearTimeout(t);
  }, [passed]);

  const detectorBroken = cam === 'live' && !!check.error && !check.ready;
  const guideState = passed ? 'ok' : check.faceVisible ? 'holding' : 'searching';
  const progress = passed ? 1 : Math.min(1, check.presentForMs / GATE_PRESENT_MS);

  let status: string;
  if (passed) status = 'Got you — let’s go';
  else if (cam === 'starting') status = 'Starting your camera…';
  else if (cam === 'error') status = 'We need your camera to match you';
  else if (detectorBroken) status = 'Face check couldn’t start on this device';
  else if (!check.ready) status = 'Getting face check ready…';
  else if (check.faceVisible) status = 'Hold still…';
  else status = 'We can’t see your face yet — move into the oval';

  const finish = () => {
    stopStream(streamRef.current);
    streamRef.current = null;
    onPassRef.current();
  };

  return (
    <div className={cn('mx-auto w-full max-w-md', className)} data-testid="face-gate">
      <h2 className="text-center font-display text-[40px] leading-[0.9] sm:text-[48px]">{title}</h2>
      <p className="mx-auto mt-2 max-w-sm text-center text-[15px] text-muted">{sub}</p>
      <div className={cn('relative mx-auto mt-6 aspect-[4/5] w-full max-w-[320px] overflow-hidden rounded-card bg-night outline outline-[3px] outline-offset-0 transition-[outline-color] duration-150', passed || check.faceVisible ? 'outline-green' : 'outline-transparent')}>
        <video ref={videoRef} autoPlay playsInline muted className={cn('mirror h-full w-full object-cover', cam !== 'live' && 'invisible')} data-testid="face-gate-preview" />
        {cam === 'live' && <Guide state={guideState} progress={progress} />}
        {cam === 'starting' && (
          <div className="absolute inset-0 flex items-center justify-center text-bone/70">
            <Icon name="cam" size={32} />
          </div>
        )}
        {cam === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center text-bone/70">
            <Icon name="cam-off" size={32} />
          </div>
        )}
        {passed && (
          <motion.span initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', duration: 0.3, bounce: 0 }} className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-green text-night">
            <Icon name="check" size={32} strokeWidth={3} />
          </motion.span>
        )}
      </div>
      <p
        className={cn('mx-auto mt-4 w-fit text-balance text-center text-[15px] font-semibold', passed || check.faceVisible ? 'text-go' : cam === 'error' ? 'text-danger' : 'text-fg')}
        role="status"
        aria-live="polite"
        data-testid="face-gate-status"
        data-state={passed ? 'passed' : check.faceVisible ? 'visible' : cam}
      >
        {status}
      </p>
      {cam === 'error' && camErr && (
        <div className="mt-4 space-y-3">
          <PermissionHelp kind={camErr} />
          <Button block variant="outline" accent="paper" icon="flip" onClick={() => void start()}>
            Try again
          </Button>
        </div>
      )}
      {detectorBroken && (
        <div className="mt-4 text-center">
          <p className="text-[15px] text-muted">Your browser couldn’t run face check. You can still continue — please keep your face on camera.</p>
          <Button className="mt-3" accent="paper" onClick={finish} data-testid="face-gate-continue">
            Continue
          </Button>
        </div>
      )}
      <p className="mx-auto mt-5 max-w-sm text-center text-[13px] text-muted">
        <Icon name="shield" size={14} className="mr-1 inline-block align-[-2px]" />
        {FACE_PRIVACY}
      </p>
    </div>
  );
}

/**
 * Resolve true once the current guidelines are accepted and a face is verified (or the check is off / was satisfied
 * recently). Used before invites and Drop pods; the Match screen hosts its own gate.
 */
export async function requireFace(title = 'Quick face check'): Promise<boolean> {
  // Guidelines v2 first (SPEC_V4 §7): every caller connects you to someone.
  if (!(await requireGuidelines())) return false;
  if (faceCheckDevOff()) return true;
  let cfg = queryClient.getQueryData<AppConfig>(qk.config);
  if (!cfg) cfg = await queryClient.fetchQuery({ queryKey: qk.config, queryFn: endpoints.config }).catch(() => undefined);
  if (cfg?.face_check === 'off') return true;
  if (recentlyVerified()) return true;
  return new Promise<boolean>((resolve) => useFaceStore.getState().open({ title, resolve }));
}

/** Global sheet that hosts `requireFace()` requests. */
export function FaceGateHost() {
  const pending = useFaceStore((s) => s.pending);
  const settle = useFaceStore((s) => s.settle);
  return (
    <Sheet open={!!pending} onClose={() => settle(false)} testId="face-gate-sheet" maxHeight="94vh">
      {pending && <FaceGate title={pending.title} sub="Just a second on camera before you connect. Calls on Popin are face to face." onPass={() => settle(true)} />}
    </Sheet>
  );
}
